import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  WEBSITE_CANVAS_GRANT_REFRESH_LEAD_MS,
  WebsiteTemplateServiceError,
  isWebsiteCanvasSessionMessage,
  parseWebsiteCanvasMessage,
  parseWebsiteCanvasStorePath,
  websiteCanvasBridge,
  websiteCanvasHomePath,
  websiteCanvasPathForPageKind,
  type PublicStoreWebsiteDto,
  type WebsiteCanvasDraftBrowseGrant,
  type WebsiteCanvasErrorCode,
  type WebsiteCanvasMode,
  type WebsiteCanvasNavigateTarget,
  type WebsiteCanvasOverlayLabels,
  type WebsiteCanvasRegionChrome,
  type WebsiteCanvasRegionReport,
  type WebsiteCanvasSession,
  type WebsiteCanvasStoreIdentity,
  type WebsiteInterfaceLocale,
  type WebsitePageKind,
} from '@dukanti/shared';
import type { CanvasOrigins } from '../lib/admin-origins';

/**
 * The Admin side of the canvas bridge, protocol v2 (C8). It frames the
 * storefront canvas document, opens a session with it, answers every route the
 * canvas reports with the draft page of that route, and checks every intent
 * the canvas sends before it reaches the editor.
 *
 * Trust: a message counts only from the exact storefront origin AND the
 * window of the iframe this hook renders, is parsed with the shared parser
 * for this store, and (but for `ready` and `error`) belongs to the session
 * the host opened last.
 */

export type CanvasHostStatus = 'loading' | 'ready' | 'error';
export type CanvasHostErrorReason = 'config' | 'timeout' | 'grant' | 'page' | 'canvas';

export interface CanvasRouteState {
  /** The path the canvas reported: a store path, or another in-app path. */
  readonly path: string | null;
  /** Null when the route is not a page of the editor. */
  readonly pageKind: WebsitePageKind | null;
  /** The store-relative address (`/`, `/p/<id>`) for the read-only address bar. */
  readonly displayPath: string | null;
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
}

export interface CanvasGrantSource {
  readonly mint: () => Promise<WebsiteCanvasDraftBrowseGrant>;
  /** Best effort. The host never waits for it and ignores its failure. */
  readonly revoke: (token: string) => Promise<void>;
}

/** One page of the draft as the canvas needs it: the wire DTO, and the public identity of the store. */
export interface CanvasPageData {
  readonly dto: PublicStoreWebsiteDto;
  readonly store: WebsiteCanvasStoreIdentity;
}

export interface UseStorefrontCanvasHostOptions {
  readonly storeSlug: string;
  /** Null when the environment does not give two valid origins. */
  readonly origins: CanvasOrigins | null;
  readonly mode: WebsiteCanvasMode;
  readonly adminLocale: WebsiteInterfaceLocale;
  readonly overlayLabels: WebsiteCanvasOverlayLabels;
  readonly editMode: boolean;
  /** True while any Admin modal is open: the canvas is inert and refuses navigation. */
  readonly locked: boolean;
  /** Changes with every draft revision (or demo template version): a new generation. */
  readonly contentKey: string;
  /** `template-demo`: the first page. */
  readonly initialPageKind?: WebsitePageKind;
  /** One page kind, fresh from the server. */
  readonly loadPage: (pageKind: WebsitePageKind) => Promise<CanvasPageData>;
  /** Regions of chrome and one page, labelled in the Admin language. `[]` for the demo. */
  readonly regionsFor: (pageKind: WebsitePageKind) => readonly WebsiteCanvasRegionChrome[];
  /** The manifest check of the intent acceptance rule (1). */
  readonly acceptsRegion?: (regionId: string, pageKind: WebsitePageKind) => boolean;
  readonly onRegionEditIntent?: (regionId: string, pageKind: WebsitePageKind) => void;
  readonly onNavigationBlocked?: () => void;
  /** Required in `store-draft`, unused in `template-demo`. */
  readonly grants?: CanvasGrantSource;
  readonly readyTimeoutMs?: number;
  /** How many times `render_failed` starts a new session by itself within a minute. */
  readonly maxAutoRestarts?: number;
}

export type CanvasReportedRegion = WebsiteCanvasRegionReport & {
  readonly label: string;
};

export interface CanvasHost {
  readonly iframeRef: RefObject<HTMLIFrameElement>;
  /** The React key of the iframe element. A new value is a new element instance. */
  readonly frameKey: number;
  readonly frameSrc: string | null;
  readonly status: CanvasHostStatus;
  readonly errorReason: CanvasHostErrorReason | null;
  /** Closed unsupported-template signal; generic transport failures retain their normal retry. */
  readonly runtimeUnavailable?: boolean;
  readonly route: CanvasRouteState;
  /** Latest current-page report, labelled only from the pinned manifest. */
  readonly reportedRegions: readonly CanvasReportedRegion[];
  /** Same live-session, page-membership, manifest and modal checks as a canvas intent. */
  readonly editRegion: (regionId: string) => void;
  /** The id the canvas gave itself in its last `ready`. Only for evidence. */
  readonly canvasInstanceId: string | null;
  /** The frame was reloaded because its document changed under us. */
  readonly resetNotice: boolean;
  readonly dismissResetNotice: () => void;
  readonly onFrameLoad: () => void;
  readonly navigate: (target: WebsiteCanvasNavigateTarget) => void;
  readonly focusRegion: (regionId: string) => void;
  /** A new element instance, at the last store path. The draft cart of the frame starts empty. */
  readonly refresh: () => void;
}

export const DEFAULT_READY_TIMEOUT_MS = 15_000;
const DEFAULT_MAX_AUTO_RESTARTS = 2;
const AUTO_RESTART_WINDOW_MS = 60_000;
const GRANT_RETRY_MS = 30_000;

const NO_ROUTE: CanvasRouteState = {
  path: null,
  pageKind: null,
  displayPath: null,
  canGoBack: false,
  canGoForward: false,
};

interface LiveSession {
  readonly storeSlug: string;
  readonly storefrontOrigin: string | null;
  readonly session: WebsiteCanvasSession;
  sequence: number;
  grant: WebsiteCanvasDraftBrowseGrant | null;
  grantTimer: ReturnType<typeof setTimeout> | null;
  routeKind: WebsitePageKind | null;
  readonly sentGeneration: Map<WebsitePageKind, number>;
  readonly inflight: Set<string>;
  readonly regions: Map<WebsitePageKind, ReadonlySet<string>>;
  readonly pagesBySequence: Map<
    number,
    { readonly kind: WebsitePageKind; readonly generation: number }
  >;
  readonly acknowledgedGeneration: Map<WebsitePageKind, number>;
  supporting: { readonly generation: number; status: 'pending' | 'failed' | 'ready' } | null;
}

function supportBlocks(live: LiveSession, generation: number): boolean {
  return (
    live.supporting !== null &&
    (live.supporting.generation !== generation ||
      live.supporting.status !== 'ready' ||
      (live.routeKind !== null && live.acknowledgedGeneration.get(live.routeKind) !== generation))
  );
}

export function useStorefrontCanvasHost(options: UseStorefrontCanvasHostOptions): CanvasHost {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const { origins, storeSlug } = options;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const liveRef = useRef<LiveSession | null>(null);
  const startCountRef = useRef(0);
  const generationRef = useRef(1);
  const lastPathRef = useRef<string | null>(null);
  const loadsRef = useRef(0);
  const lockedRef = useRef(options.locked);
  const restartsRef = useRef<number[]>([]);
  const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runtimeUnavailableRef = useRef(false);

  const [frameKey, setFrameKey] = useState(0);
  const [status, setStatus] = useState<CanvasHostStatus>(origins === null ? 'error' : 'loading');
  const [errorReason, setErrorReason] = useState<CanvasHostErrorReason | null>(
    origins === null ? 'config' : null,
  );
  const [route, setRoute] = useState<CanvasRouteState>(NO_ROUTE);
  const [canvasInstanceId, setCanvasInstanceId] = useState<string | null>(null);
  const [resetNotice, setResetNotice] = useState(false);
  const [regionReports, setRegionReports] = useState<readonly WebsiteCanvasRegionReport[]>([]);
  const [runtimeUnavailable, setRuntimeUnavailable] = useState(false);

  const post = useCallback(
    (message: unknown): void => {
      const target = iframeRef.current?.contentWindow;
      if (!target || !origins) return;
      // Only ever to the storefront origin: a message for the canvas is never broadcast.
      target.postMessage(message, origins.storefrontOrigin);
    },
    [origins],
  );

  const clearReadyTimer = useCallback((): void => {
    if (readyTimerRef.current !== null) {
      clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
  }, []);

  const fail = useCallback(
    (reason: CanvasHostErrorReason, incompatibleRuntime = false): void => {
      const live = liveRef.current;
      if (live?.supporting) {
        live.supporting.status = 'failed';
        if (iframeRef.current) iframeRef.current.inert = true;
        post(websiteCanvasBridge.makeInteractionLockMessage(live.session, true));
      }
      clearReadyTimer();
      if (incompatibleRuntime) {
        runtimeUnavailableRef.current = true;
        setRuntimeUnavailable(true);
      }
      setStatus('error');
      setErrorReason(reason);
    },
    [clearReadyTimer, post],
  );

  const armReadyTimer = useCallback((): void => {
    clearReadyTimer();
    if (optionsRef.current.origins === null) return;
    const timeout = optionsRef.current.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;
    readyTimerRef.current = setTimeout(() => {
      readyTimerRef.current = null;
      fail('timeout');
    }, timeout);
  }, [clearReadyTimer, fail]);

  const revokeQuietly = useCallback((token: string): void => {
    const grants = optionsRef.current.grants;
    if (!grants) return;
    void grants.revoke(token).catch(() => undefined);
  }, []);

  const endSession = useCallback((): void => {
    setRegionReports([]);
    const live = liveRef.current;
    if (!live) return;
    if (live.grantTimer !== null) clearTimeout(live.grantTimer);
    if (live.grant) revokeQuietly(live.grant.token);
    liveRef.current = null;
  }, [revokeQuietly]);

  const scheduleGrantRefresh = useCallback(
    (live: LiveSession, retry: boolean): void => {
      const grants = optionsRef.current.grants;
      if (!grants || !live.grant) return;
      if (live.grantTimer !== null) clearTimeout(live.grantTimer);
      const untilExpiry = Date.parse(live.grant.expiresAt) - Date.now();
      const delay = retry
        ? GRANT_RETRY_MS
        : Math.max(0, untilExpiry - WEBSITE_CANVAS_GRANT_REFRESH_LEAD_MS);
      live.grantTimer = setTimeout(() => {
        live.grantTimer = null;
        void grants.mint().then(
          (next) => {
            if (liveRef.current !== live) {
              revokeQuietly(next.token);
              return;
            }
            const previous = live.grant;
            live.grant = next;
            post(websiteCanvasBridge.makeDraftBrowseGrantMessage(live.session, next));
            if (previous) revokeQuietly(previous.token);
            scheduleGrantRefresh(live, false);
          },
          () => {
            if (liveRef.current !== live) return;
            if (live.supporting) fail('grant');
            else scheduleGrantRefresh(live, true);
          },
        );
      }, delay);
    },
    [post, revokeQuietly, fail],
  );

  const ensurePage = useCallback(
    function ensure(pageKind: WebsitePageKind): void {
      const live = liveRef.current;
      if (!live) return;
      const generation = generationRef.current;
      const contentKey = optionsRef.current.contentKey;
      if (live.sentGeneration.get(pageKind) === generation) return;
      const inflightKey = `${pageKind}:${generation}`;
      if (live.inflight.has(inflightKey)) return;
      live.inflight.add(inflightKey);

      void optionsRef.current.loadPage(pageKind).then(
        ({ dto, store }) => {
          live.inflight.delete(inflightKey);
          // The session ended, or the draft moved on and a newer request owns this page.
          if (
            liveRef.current !== live ||
            generationRef.current !== generation ||
            optionsRef.current.contentKey !== contentKey
          )
            return;
          const current = optionsRef.current;
          if (
            live.storeSlug !== current.storeSlug ||
            live.storefrontOrigin !== current.origins?.storefrontOrigin
          )
            return;
          const beauty = current.mode === 'store-draft' && dto.site?.layoutFamily === 'beauty';
          if (
            (beauty || live.supporting !== null) &&
            current.mode === 'store-draft' &&
            (!live.grant || Date.parse(live.grant.expiresAt) <= Date.now())
          ) {
            if (live.supporting) live.supporting.status = 'failed';
            fail('grant');
            return;
          }
          if (!beauty && live.supporting) {
            live.supporting = null;
            if (iframeRef.current) iframeRef.current.inert = false;
            post(websiteCanvasBridge.makeInteractionLockMessage(live.session, lockedRef.current));
          }
          if (beauty && live.supporting?.generation !== generation) {
            // Block before either page of this generation reaches the frame.
            live.supporting = { generation, status: 'pending' };
            if (iframeRef.current) iframeRef.current.inert = true;
            post(websiteCanvasBridge.makeInteractionLockMessage(live.session, true));
            setStatus('loading');
            setErrorReason(null);
          }
          live.sequence += 1;
          live.sentGeneration.set(pageKind, generation);
          live.pagesBySequence.set(live.sequence, { kind: pageKind, generation });
          post(
            websiteCanvasBridge.makePageMessage(live.session, live.sequence, {
              pageKind,
              generation,
              dto,
              store,
              regions: current.mode === 'store-draft' ? [...current.regionsFor(pageKind)] : [],
            }),
          );
          if (beauty && pageKind !== 'store-info') ensure('store-info');
        },
        (error: unknown) => {
          live.inflight.delete(inflightKey);
          if (
            liveRef.current !== live ||
            generationRef.current !== generation ||
            optionsRef.current.contentKey !== contentKey
          )
            return;
          if (
            live.storeSlug !== optionsRef.current.storeSlug ||
            live.storefrontOrigin !== optionsRef.current.origins?.storefrontOrigin
          )
            return;
          if (live.supporting?.generation === generation) live.supporting.status = 'failed';
          fail(
            'page',
            error instanceof WebsiteTemplateServiceError && error.code === 'template_unavailable',
          );
        },
      );
    },
    [fail, post],
  );

  const startSession = useCallback(async (): Promise<void> => {
    const previous = liveRef.current;
    if (previous?.supporting) {
      if (iframeRef.current) iframeRef.current.inert = true;
      post(websiteCanvasBridge.makeInteractionLockMessage(previous.session, true));
      setStatus('loading');
      setErrorReason(null);
    }
    endSession();
    startCountRef.current += 1;
    const attempt = startCountRef.current;
    const current = optionsRef.current;

    let grant: WebsiteCanvasDraftBrowseGrant | null = null;
    if (current.mode === 'store-draft') {
      if (!current.grants) {
        fail('grant');
        return;
      }
      try {
        grant = await current.grants.mint();
      } catch {
        if (attempt === startCountRef.current) fail('grant');
        return;
      }
      if (attempt !== startCountRef.current) {
        revokeQuietly(grant.token);
        return;
      }
    }

    const live: LiveSession = {
      storeSlug: current.storeSlug,
      storefrontOrigin: current.origins?.storefrontOrigin ?? null,
      session: websiteCanvasBridge.createSession(),
      sequence: 0,
      grant,
      grantTimer: null,
      routeKind: null,
      sentGeneration: new Map(),
      inflight: new Set(),
      regions: new Map(),
      pagesBySequence: new Map(),
      acknowledgedGeneration: new Map(),
      supporting: null,
    };
    liveRef.current = live;

    const demoPath =
      current.mode === 'template-demo' && current.initialPageKind
        ? websiteCanvasPathForPageKind(current.initialPageKind, current.storeSlug)
        : null;
    const latest = optionsRef.current;
    post(
      websiteCanvasBridge.makeInitMessage(live.session, {
        storeSlug: latest.storeSlug,
        mode: latest.mode,
        adminLocale: latest.adminLocale,
        initialPath: lastPathRef.current ?? demoPath ?? websiteCanvasHomePath(latest.storeSlug),
        editMode: latest.editMode,
        overlayLabels: latest.overlayLabels,
        draftBrowseGrant: grant,
      }),
    );
    // A session starts unlocked on the canvas side; a modal that is open stays open.
    if (lockedRef.current) {
      post(websiteCanvasBridge.makeInteractionLockMessage(live.session, true));
    }
    scheduleGrantRefresh(live, false);
  }, [endSession, fail, post, revokeQuietly, scheduleGrantRefresh]);

  const remount = useCallback((): void => {
    endSession();
    startCountRef.current += 1; // a start still waiting for its grant is void
    loadsRef.current = 0;
    setFrameKey((key) => key + 1);
    setStatus('loading');
    setErrorReason(null);
    runtimeUnavailableRef.current = false;
    setRuntimeUnavailable(false);
    setRoute(NO_ROUTE);
    armReadyTimer();
  }, [armReadyTimer, endSession]);

  const refresh = useCallback((): void => {
    setResetNotice(false);
    remount();
  }, [remount]);

  const scopeSeen = useRef({ storeSlug, origin: origins?.storefrontOrigin });
  useEffect(() => {
    const next = { storeSlug, origin: origins?.storefrontOrigin };
    if (scopeSeen.current.storeSlug === next.storeSlug && scopeSeen.current.origin === next.origin)
      return;
    scopeSeen.current = next;
    lastPathRef.current = null;
    remount();
  }, [storeSlug, origins?.storefrontOrigin, remount]);

  // Backstop 4: the first `load` of an iframe element is its own document,
  // whether it fires before or after `ready`. Any later `load` of the same
  // element means the document changed under us.
  const onFrameLoad = useCallback((): void => {
    loadsRef.current += 1;
    if (loadsRef.current > 1) {
      setResetNotice(true);
      remount();
    }
  }, [remount]);

  const navigate = useCallback(
    (target: WebsiteCanvasNavigateTarget): void => {
      const live = liveRef.current;
      if (!live || lockedRef.current || supportBlocks(live, generationRef.current)) return;
      post(websiteCanvasBridge.makeNavigateMessage(live.session, target));
    },
    [post],
  );

  const focusRegion = useCallback(
    (regionId: string): void => {
      const live = liveRef.current;
      if (!live || supportBlocks(live, generationRef.current)) return;
      post(websiteCanvasBridge.makeFocusRegionMessage(live.session, regionId));
    },
    [post],
  );

  const editRegion = useCallback((regionId: string): void => {
    const live = liveRef.current;
    const current = optionsRef.current;
    const pageKind = live?.routeKind ?? null;
    // The toolbar and the canvas use one acceptance rule: current session,
    // current page report, pinned manifest and no open Admin modal.
    if (
      !live ||
      pageKind === null ||
      lockedRef.current ||
      current.locked ||
      supportBlocks(live, generationRef.current)
    )
      return;
    if (
      live.storeSlug !== current.storeSlug ||
      live.storefrontOrigin !== current.origins?.storefrontOrigin
    )
      return;
    if (
      !current.editMode ||
      current.mode !== 'store-draft' ||
      !live.grant ||
      !(Date.parse(live.grant.expiresAt) > Date.now())
    )
      return;
    if (!live.regions.get(pageKind)?.has(regionId)) return;
    if (current.acceptsRegion && !current.acceptsRegion(regionId, pageKind)) return;
    current.onRegionEditIntent?.(regionId, pageKind);
  }, []);

  // Messages of the canvas.
  useEffect(() => {
    if (!origins) return undefined;

    const onCanvasError = (code: WebsiteCanvasErrorCode): void => {
      if (code === 'unsupported_template') {
        fail('canvas', true);
        return;
      }
      // A closed compatibility failure needs a deliberate new document, never a render restart loop.
      if (runtimeUnavailableRef.current) {
        fail('canvas');
        return;
      }
      if (code === 'render_failed') {
        const now = Date.now();
        restartsRef.current = restartsRef.current.filter((at) => now - at < AUTO_RESTART_WINDOW_MS);
        const limit = optionsRef.current.maxAutoRestarts ?? DEFAULT_MAX_AUTO_RESTARTS;
        if (restartsRef.current.length < limit) {
          restartsRef.current.push(now);
          void startSession();
          return;
        }
      }
      fail('canvas');
    };

    const onMessage = (event: MessageEvent): void => {
      const trusted = websiteCanvasBridge.isTrustedMessageEvent({
        event,
        expectedOrigin: origins.storefrontOrigin,
        expectedSource: iframeRef.current?.contentWindow ?? null,
      });
      if (!trusted) return;
      const message = parseWebsiteCanvasMessage(event.data, { storeSlug });
      if (!message) return;

      if (message.type === 'canvas.websiteReady') {
        if (runtimeUnavailableRef.current) return;
        clearReadyTimer();
        setCanvasInstanceId(message.canvasInstanceId);
        void startSession();
        return;
      }
      if (message.type === 'canvas.websiteError') {
        onCanvasError(message.code);
        return;
      }

      const live = liveRef.current;
      if (!live || !isWebsiteCanvasSessionMessage(message, live.session)) return;
      const current = optionsRef.current;

      switch (message.type) {
        case 'canvas.websiteAck':
          if (runtimeUnavailableRef.current) return;
          if (live.supporting) {
            if (!live.grant || Date.parse(live.grant.expiresAt) <= Date.now()) {
              fail('grant');
              return;
            }
            const page = live.pagesBySequence.get(message.sequence);
            const generation = generationRef.current;
            if (
              !page ||
              page.generation !== generation ||
              live.supporting.generation !== generation
            )
              return;
            live.acknowledgedGeneration.set(page.kind, generation);
            if (page.kind === 'store-info' && live.supporting.status !== 'failed') {
              live.supporting.status = 'ready';
            }
            if (
              live.supporting.status === 'ready' &&
              (live.routeKind === null ||
                live.acknowledgedGeneration.get(live.routeKind) === generation)
            ) {
              setStatus('ready');
              setErrorReason(null);
              if (iframeRef.current) iframeRef.current.inert = false;
              post(websiteCanvasBridge.makeInteractionLockMessage(live.session, lockedRef.current));
            }
            return;
          }
          if (message.sequence <= live.sequence) {
            setStatus('ready');
            setErrorReason(null);
            if (iframeRef.current) iframeRef.current.inert = false;
          }
          return;
        case 'canvas.routeChanged': {
          // Same-kind navigation retains the manifest's region membership.
          // The canvas can deduplicate an unchanged geometry report; clearing
          // it here would disable valid controls until a later layout change.
          if (live.routeKind !== message.pageKind) {
            setRegionReports([]);
            live.regions.clear();
          }
          live.routeKind = message.pageKind;
          if (
            live.supporting &&
            live.supporting.status !== 'failed' &&
            message.pageKind &&
            live.acknowledgedGeneration.get(message.pageKind) !== generationRef.current
          ) {
            setStatus('loading');
            if (iframeRef.current) iframeRef.current.inert = true;
            post(websiteCanvasBridge.makeInteractionLockMessage(live.session, true));
          }
          const store =
            message.pageKind === null
              ? null
              : parseWebsiteCanvasStorePath(message.path, current.storeSlug);
          if (store) lastPathRef.current = store.path;
          setRoute({
            path: message.path,
            pageKind: message.pageKind,
            displayPath: store?.displayPath ?? message.path,
            canGoBack: message.canGoBack,
            canGoForward: message.canGoForward,
          });
          if (message.pageKind) ensurePage(message.pageKind);
          return;
        }
        case 'canvas.regionsChanged':
          live.regions.set(message.pageKind, new Set(message.regions.map((r) => r.regionId)));
          if (message.pageKind === live.routeKind) setRegionReports(message.regions);
          return;
        case 'canvas.regionEditIntent':
          editRegion(message.regionId);
          return;
        case 'canvas.navigationBlocked':
          current.onNavigationBlocked?.();
          return;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origins, storeSlug, clearReadyTimer, ensurePage, fail, startSession, editRegion, post]);

  // The first frame gets its ready timeout; the last one is closed with the host.
  useEffect(() => {
    armReadyTimer();
    return () => {
      // A grant minted after unmount must never revive this host or its refresh timer.
      startCountRef.current += 1;
      clearReadyTimer();
      endSession();
    };
  }, [armReadyTimer, clearReadyTimer, endSession]);

  // Edit mode toggles the overlay of the canvas. No re-init, no refetch.
  const { editMode } = options;
  const editModeSeen = useRef(editMode);
  useEffect(() => {
    if (editModeSeen.current === editMode) return;
    editModeSeen.current = editMode;
    const live = liveRef.current;
    if (live) post(websiteCanvasBridge.makeEditModeMessage(live.session, editMode));
  }, [editMode, post]);

  const { locked } = options;
  useEffect(() => {
    lockedRef.current = locked;
    const live = liveRef.current;
    if (live)
      post(
        websiteCanvasBridge.makeInteractionLockMessage(
          live.session,
          locked || supportBlocks(live, generationRef.current),
        ),
      );
  }, [locked, post]);

  // A new draft revision is a new generation: the page of the current route is
  // sent again, every other page counts as not sent.
  const { contentKey } = options;
  const contentKeySeen = useRef(contentKey);
  useEffect(() => {
    if (contentKeySeen.current === contentKey) return;
    contentKeySeen.current = contentKey;
    setRegionReports([]);
    liveRef.current?.regions.clear();
    liveRef.current?.pagesBySequence.clear();
    liveRef.current?.acknowledgedGeneration.clear();
    generationRef.current += 1;
    if (liveRef.current?.supporting) {
      liveRef.current.supporting = { generation: generationRef.current, status: 'pending' };
      if (iframeRef.current) iframeRef.current.inert = true;
      post(websiteCanvasBridge.makeInteractionLockMessage(liveRef.current.session, true));
      setStatus('loading');
      setErrorReason(null);
    }
    const kind = liveRef.current?.routeKind ?? null;
    if (kind) ensurePage(kind);
  }, [contentKey, ensurePage, post]);

  // The labels of the canvas are in the Admin language: a new language is a new session.
  const { adminLocale } = options;
  const localeSeen = useRef(adminLocale);
  useEffect(() => {
    if (localeSeen.current === adminLocale) return;
    localeSeen.current = adminLocale;
    if (liveRef.current) void startSession();
  }, [adminLocale, startSession]);

  const frameSrc = useMemo(
    () =>
      origins ? `${origins.storefrontOrigin}${websiteCanvasBridge.canvasPath(storeSlug)}` : null,
    [origins, storeSlug],
  );

  const dismissResetNotice = useCallback((): void => setResetNotice(false), []);

  const { regionsFor, acceptsRegion } = options;
  const reportedRegions = useMemo((): readonly CanvasReportedRegion[] => {
    const pageKind = route.pageKind;
    if (pageKind === null || liveRef.current?.storeSlug !== storeSlug) return [];
    const labels = new Map(regionsFor(pageKind).map((region) => [region.regionId, region.label]));
    return regionReports.flatMap((region) => {
      const label = labels.get(region.regionId);
      return label === undefined || (acceptsRegion && !acceptsRegion(region.regionId, pageKind))
        ? []
        : [{ ...region, label }];
    });
  }, [regionReports, route.pageKind, regionsFor, acceptsRegion, storeSlug]);

  return {
    iframeRef,
    frameKey,
    frameSrc,
    status,
    errorReason,
    runtimeUnavailable,
    route,
    reportedRegions,
    editRegion,
    canvasInstanceId,
    resetNotice,
    dismissResetNotice,
    onFrameLoad,
    navigate,
    focusRegion,
    refresh,
  };
}
